<template>
    <div class="main">
        <div class="bg-white">

            <!-- Hero & active addresses toggle -->
            <div class="container accountDeliveryAddresses__container mx-auto text-center p-4 py-0">
                <img class="accountDeliveryAddressesHero__image" height="125"
                     src="~@/assets/images/pages/account/my-delivery-addresses/house.svg" alt="">
                <div class="w-full pt-3 pb-0 flex justify-center items-center accountDeliveryAddresses__toggleContainer"
                     @click="toggleActiveSubscription">
                    <div>&nbsp;</div>
                    <h2 class="pl-6 pr-2" v-text="$t('MY_DELIVERY_ADDRESSES_CURRENT_ADDRESS')"></h2>
                    <img :class="activeToggleClass"
                         src="@/assets/images/decoration/arrow-down-black.png">
                </div>
            </div>

            <!-- Active addresses -->
            <div class="background--paper divider--container py-4">
                <div class="divider-white--top"></div>
                <div :class="activeContainerClass">
                    <div class="container accountDeliveryAddresses__container
                    mx-auto text-center pt-4 pb-12 px-4">
                        <address-list
                            :subscriptions="activeSubscriptions"
                            @closeList="toggleActiveSubscription"
                            type="active">
                        </address-list>

                        <!-- Create new subscription cta -->
                        <div class="accountDeliveryAddresses__createCtaContainer">
                            <div v-if="showCreateAddressForm === false"
                                 class="accountDeliveryAddresses__createCtaCtaContainer">
                                <div class="accountDeliveryAddresses__createCtaIcon"></div>
                                <p class="ml-1 m-0 mt-1">
                                    <a href="#" @click.prevent="toggleCreateForm">
                                        {{ $t('MY_DELIVERY_ADDRESSES_SEND_FLOWERS') }}
                                    </a>
                                </p>
                            </div>
                            <div :class="createSubscriptionAccordionClass">
                                <create-subscription-form
                                    @closed="toggleCreateForm"
                                    :key="showCreateAddressForm">
                                </create-subscription-form>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="divider-white--bottom"></div>
            </div>

            <!-- Saved addresses toggle -->
            <div v-if="inactiveSubscriptions.length > 0" class="bg-white">
                <div class="container accountDeliveryAddresses__container mx-auto">
                    <div class="w-full pt-3 pb-0 flex justify-center items-center
                    accountDeliveryAddresses__toggleContainer"
                         @click="toggleInactiveSubscriptions">
                        <h2 class="pl-6 pr-2">
                            {{ $t('MY_DELIVERY_ADDRESSES_SAVED_ADDRESSES') }}
                        </h2>
                        <img :class="inactiveToggleClass"
                             src="@/assets/images/decoration/arrow-down-black.png">
                    </div>
                </div>
            </div>

            <!-- Saved addresses -->
            <div v-if="inactiveSubscriptions.length > 0" class="background--paper divider--container py-4">
                <div class="divider-white--top"></div>
                <div :class="inactiveContainerClass">
                    <div class="container accountDeliveryAddresses__container
                    mx-auto text-center pt-4 pb-12 px-4">
                        <address-list
                            :unique="true"
                            :subscriptions="inactiveSubscriptions"
                            @closeList="toggleInactiveSubscriptions"
                            type="inactive">
                        </address-list>
                    </div>
                </div>
                <div class="divider-white--bottom"></div>
            </div>
        </div>

        <!-- Billing details -->
        <div class="bg-white pl-8 pr-8 pt-16 pb-20" id="manageBllingContainer">
            <div class="container accountDeliveryAddresses__container mx-auto text-center">
                <h2 class="px-2">
                    {{ $t('MY_DELIVERY_ADDRESSES_BILLING_ADDRESS') }}
                </h2>
                <manage-billing-address></manage-billing-address>

                <a class="link" @click="goToCardDetailsPage">
                    {{ $t('MY_DELIVERY_ADDRESSES_MANAGE') }}
                </a>
            </div>
        </div>
        <floating-banner :customStyle="customStyle"/>
        <cannot-pause-modal v-if="!canPauseOrCancel"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CreateSubscriptionForm from '@/components/forms/subscriptions/CreateSubscriptionForm';
import AddressList from '@/components/partials/account/my-delivery-addresses/AddressList';
import ManageBillingAddress from '@/components/partials/account/my-delivery-addresses/ManageBillingAddress';
import FloatingBanner from '@/components/structure/FloatingBanner.vue';
import CannotPauseModal from '@/components/modals/CannotPauseModal';

export default {
    name: 'MyDeliveryAddresses',
    data() {
        return {
            showActiveSubscriptions: true,
            showInactiveSubscriptions: false,
            showCreateAddressForm: false,
        };
    },
    created() {
        setTimeout(() => {
            this.$store.dispatch('subscription/fetchAllSubscriptions');
        }, 250);
    },
    methods: {
        toggleCreateForm() {
            this.showCreateAddressForm = !this.showCreateAddressForm;
        },

        toggleActiveSubscription() {
            this.showActiveSubscriptions = !this.showActiveSubscriptions;
        },

        toggleInactiveSubscriptions() {
            this.showInactiveSubscriptions = !this.showInactiveSubscriptions;
        },

        getAccordionClassBasedOnSubCount(subCount) {
            switch (subCount) {
            case 1:
                return 'accordion__lg';
            case 2:
                return 'accordion__xl';
            default:
                return 'accordion__xxl';
            }
        },

        goToCardDetailsPage() {
            this.expandFormName = 'billingAddressForm';
            this.$router.push({ name: 'account.manage-account' });
        },
    },
    computed: {
        ...mapGetters({
            activeSubscriptions: 'subscription/getActiveSubscriptions',
            getInactiveSubscriptions: 'subscription/getInactiveSubscriptions',
            canPauseOrCancel: 'account/getCanPauseOrCancel',
            getAddress: 'address/getAddressById',
            addresses: 'address/getAddresses',
        }),

        inactiveSubscriptions() {
            if (!this.addresses) return [];
            let activeAddress = this.addresses?.filter(item => item.is_active);
            let activeSummaryString = [];

            activeAddress.forEach(address => {
                if (!activeSummaryString.includes(address.summary_string)) {
                    activeSummaryString.push(address.summary_string);
                }
            });

            let filteredSubs = this.getInactiveSubscriptions.filter(item => {
                let summaryString = this.getAddress(item.delivery_address_id).summary_string;
                if (activeSummaryString.includes(summaryString)) return false;
                return true;
            });

            return filteredSubs || [];
        },

        activeToggleClass() {
            return this.showActiveSubscriptions
                ? 'accountDeliveryAddresses__toggleIcon accountDeliveryAddresses__toggleIcon--active'
                : 'accountDeliveryAddresses__toggleIcon';
        },
        activeContainerClass() {
            let cssClass = this.getAccordionClassBasedOnSubCount(this.activeSubscriptions.length);

            return this.showActiveSubscriptions ? `${cssClass} ${cssClass}--active` : cssClass;
        },
        createSubscriptionAccordionClass() {
            return this.showCreateAddressForm ? 'accordion__lg accordion__lg--active' : 'accordion__lg';
        },
        expandFormName: {
            get() {
                return this.$store.getters['account/getExpandFormName'];
            },
            set(value) {
                this.$store.commit('account/updateExpandFormName', value);
            },
        },
        inactiveToggleClass() {
            return this.showInactiveSubscriptions
                ? 'accountDeliveryAddresses__toggleIcon accountDeliveryAddresses__toggleIcon--active'
                : 'accountDeliveryAddresses__toggleIcon';
        },
        inactiveContainerClass() {
            return this.showInactiveSubscriptions ? 'accordion__lg accordion__lg--active' : 'accordion accordion__lg';
        },
        customStyle() {
            return {
                top: '8%',
            };
        },
    },
    components: {
        AddressList,
        CreateSubscriptionForm,
        ManageBillingAddress,
        FloatingBanner,
        CannotPauseModal,
    },
};
</script>
